import { gratuityConfig } from '~/config/gratuityConfig'
import type { GratuitySelection } from '~/types/GratuitySelection'
import { CurrencyAmount } from '~/utils/CurrencyAmount'
import { tipLimit } from '~/utils/tipLimit'
import { tipAmountForPercent } from '~/utils/tipAmountForPercent'

const subtotalOf = (subtotal: number | string): number => Number.parseFloat(String(subtotal)) || 0

/** Rounds an amount to the active currency's minor unit. */
const roundAmount = (amount: number): number =>
  Number(amount.toFixed(CurrencyAmount.minorUnits(CurrencyAmount.defaultCurrency)))

/** The currency step of one minor unit, e.g. 0.01 for USD. */
const amountStep = (): number => 10 ** -CurrencyAmount.minorUnits(CurrencyAmount.defaultCurrency)

/** The gratuity amount a selection charges on a subtotal, capped at the API tip limit. */
const amountOf = (selection: GratuitySelection, subtotal: number | string): number =>
  Math.min(
    tipLimit,
    selection.mode === 'amount'
      ? roundAmount(selection.amount)
      : tipAmountForPercent(subtotal, selection.percent)
  )

/** The percentage of the subtotal the charged amount represents; 0 when the subtotal is 0. */
const percentOf = (selection: GratuitySelection, subtotal: number | string): number => {
  const base = subtotalOf(subtotal)
  const charged = amountOf(selection, subtotal)
  if (
    selection.mode === 'percent' &&
    tipAmountForPercent(subtotal, selection.percent) === charged
  ) {
    return selection.percent
  }
  return base > 0 ? (charged / base) * 100 : 0
}

/** A selection limited to what can be charged, so what is stored is what the customer sees. */
const withinLimit = (
  selection: GratuitySelection,
  subtotal: number | string
): GratuitySelection => {
  if (selection.mode === 'amount') {
    return { amount: Math.min(tipLimit, roundAmount(selection.amount)), mode: 'amount' }
  }
  const base = subtotalOf(subtotal)
  return base > 0 && tipAmountForPercent(subtotal, selection.percent) > tipLimit
    ? { mode: 'percent', percent: (tipLimit / base) * 100 }
    : selection
}

/** Whether a selection can switch to percent: an amount has no percentage of a zero subtotal. */
const canSwitchTo = (
  selection: GratuitySelection,
  subtotal: number | string,
  mode: GratuitySelection['mode']
): boolean => selection.mode === mode || mode === 'amount' || subtotalOf(subtotal) > 0

/** The same gratuity expressed in the other mode, so a mode switch never changes the tip. */
const inMode = (
  selection: GratuitySelection,
  subtotal: number | string,
  mode: GratuitySelection['mode']
): GratuitySelection => {
  if (!canSwitchTo(selection, subtotal, mode) || selection.mode === mode) return selection
  return mode === 'amount'
    ? { amount: amountOf(selection, subtotal), mode }
    : { mode, percent: percentOf(selection, subtotal) }
}

/** The largest amount the slider offers: the top percentage's amount, capped at the API tip limit. */
const maxAmount = (subtotal: number | string): number =>
  Math.min(tipLimit, Math.max(tipAmountForPercent(subtotal, gratuityConfig.max), amountStep()))

export const gratuitySelection = {
  amountOf,
  amountStep,
  canSwitchTo,
  inMode,
  maxAmount,
  percentOf,
  roundAmount,
  withinLimit
}
